import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { SearchBar } from '../../../components/search-bar';
import { SearchableCombobox } from '../../../components/inputs';
import { RecordTable } from '../../../components/tables';
import { ListThumbnail } from '../../../components/images';
import {
  DuplicateAssetButton,
  DeleteAssetButton
} from '../../../components/buttons';
import {
  CannotDeleteModalComponent,
  ConfirmDeleteModalComponent,
  LoadingModalComponent
} from '../../../components/modals';
import type { TableRecord } from '../../../types/record-table';
import { useTRPCRequest, useLocalStorage } from '../../../hooks';
import {
  EntityDependencies,
  TRPCMethodEnum,
  TRPCResourceEnum
} from '../../../api/trpcApi/types';

import { Palette } from '../types';
import { toast } from 'react-toastify';
import { Title } from '../../../components/Title';
import { usePartners } from '../../partners/hooks';

interface PaletteWithPartner extends Palette {
  partner_name?: string | null;
}

export function PalettesList() {
  const navigate = useNavigate();

  const { handleTRPCRequest } = useTRPCRequest();
  const { activePartners } = usePartners();

  const partnerOptions = useMemo(
    () =>
      activePartners.map(partner => ({
        value: partner.partner_id!,
        label: partner.name
      })),
    [activePartners]
  );

  const [palettes, setPalettes] = useState<PaletteWithPartner[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useLocalStorage<string>(
    `${window.location.pathname}-partnerId`,
    'base'
  );

  // Validate selectedPartnerId and reset to 'base' if it's not a valid option
  useEffect(() => {
    if (activePartners.length === 0) {
      // Partners haven't loaded yet, skip validation
      return;
    }
    const isValidSelection =
      selectedPartnerId === 'base' ||
      activePartners.some(p => p.partner_id === selectedPartnerId);

    if (!isValidSelection) {
      setSelectedPartnerId('base');
    }
  }, [activePartners, selectedPartnerId, setSelectedPartnerId]);

  const [searchTerm, setSearchTerm] = useLocalStorage<string>(
    window.location.pathname,
    ''
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [showCannotDeleteModal, setShowCannotDeleteModal] = useState(false);
  const [dependencies, setDependencies] = useState<EntityDependencies>({});
  const [paletteToDelete, setPaletteToDelete] = useState<Palette>({
    palette_id: '',
    system_name: '',
    display_name: '',
    thumbnail_path: ''
  });

  const fetchPalettes = useCallback(
    async (options?: { search?: string; partnerId?: string }) => {
      setIsLoading(true);
      const requestBody: Record<string, unknown> = {};

      if (options?.search) {
        requestBody.keyword = options.search;
      }

      // If partnerId is 'base' or empty, show base palettes only
      // Otherwise show palettes for the specific partner
      if (!options?.partnerId || options.partnerId === 'base') {
        requestBody.include_partner_palettes = false;
      } else {
        requestBody.partner_palettes_only = true;
        requestBody.partner_id = options.partnerId;
      }

      const { res: palettes, error } = await handleTRPCRequest({
        method: TRPCMethodEnum.list,
        resourceType: TRPCResourceEnum.palettes,
        requestBody
      });

      setIsLoading(false);

      if (error) {
        navigate('/404');
        return;
      }

      setPalettes(palettes as PaletteWithPartner[]);
    },
    [handleTRPCRequest, navigate]
  );

  const handleDeletePalette = useCallback(
    (palette: TableRecord) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      event.preventDefault();
      setPaletteToDelete(palette as Palette);
      setConfirmDelete(true);
    },
    []
  );

  const handleConfirmedDeletePalette = useCallback(async () => {
    setConfirmDelete(false);
    setIsDeleting(true);
    const { res, error } = await handleTRPCRequest({
      method: TRPCMethodEnum.delete,
      resourceType: TRPCResourceEnum.palettes,
      requestBody: { palette_id: paletteToDelete.palette_id }
    });
    setIsDeleting(false);

    if (!error && res.success) {
      toast.success('Palette deleted successfully!');
      const filteredPalettes = palettes.filter(
        (palette: Palette) => palette.palette_id !== paletteToDelete.palette_id
      );
      setPalettes(filteredPalettes);
    }

    if (!res?.success) {
      setShowCannotDeleteModal(true);
      setDependencies(res.relatedEntities);
    }
  }, [palettes, paletteToDelete, handleTRPCRequest]);

  useEffect(() => {
    fetchPalettes({
      search: searchTerm || undefined,
      partnerId: selectedPartnerId
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedPartnerId]);

  const handleAddNew = useCallback(() => {
    navigate('/palettes/new');
  }, [navigate]);

  const handleDuplicatePalette = useCallback(
    (palette: TableRecord) => async (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      event.preventDefault();
      navigate(`/palettes/new`, {
        state: { paletteIdToCopy: palette.palette_id }
      });
    },
    [navigate]
  );

  if (isDeleting) {
    return <LoadingModalComponent isOpen={isDeleting} message="Deleting..." />;
  }

  if (isLoading) {
    return <LoadingModalComponent isOpen={isLoading} />;
  }

  const noResults = palettes.length === 0;
  const noSearch = searchTerm === '';

  return (
    <>
      <Title title="Palettes Search" />
      <div className="mb-4 flex items-end gap-4">
        <SearchableCombobox
          label="Partner"
          value={selectedPartnerId}
          onChange={setSelectedPartnerId}
          placeholder="Search partners..."
          defaultOption={{ value: 'base', label: 'Base' }}
          options={partnerOptions}
          className="min-w-[200px]"
        />
        <SearchBar
          searchTerm={searchTerm}
          onSearch={search => setSearchTerm(search)}
          onAddNew={handleAddNew}
          classNames={['flex-1']}
        />
      </div>

      {noResults && noSearch ? (
        <>
          <h2>No palettes exist yet.</h2>
          <p> Click "+ Add New" to create your first palette!</p>
        </>
      ) : noResults ? (
        <h2>No palettes found for "{searchTerm}".</h2>
      ) : (
        <RecordTable
          data={palettes}
          idKey="palette_id"
          rowClassNames={[
            'card',
            'mb-[6px]',
            'flex',
            'h-[54px]',
            'cursor-pointer',
            'items-center',
            'align-middle'
          ]}
          calculateRowHref={palette => `/palettes/${palette.palette_id}`}
          columns={[
            {
              columnKey: 'thumbnail_path',
              onRender: (palette: TableRecord) => (
                <ListThumbnail src={palette.thumbnail_path} />
              ),
              widthClassNames: ['w-[54px]']
            },
            {
              columnKey: 'system_name',
              header: 'System Name',
              onRender: (palette: TableRecord) => palette.system_name,
              widthClassNames: ['w-[25%]', 'ml-[30px]']
            },
            {
              columnKey: 'display_name',
              header: 'Display Name',
              onRender: (palette: TableRecord) => palette.display_name,
              widthClassNames: ['w-[25%]']
            },
            {
              columnKey: 'partner_name',
              header: 'Partner',
              onRender: (palette: TableRecord) => (
                <span
                  className={
                    palette.partner_name
                      ? 'text-gray-900'
                      : 'italic text-gray-400'
                  }
                >
                  {palette.partner_name || 'Base'}
                </span>
              ),
              widthClassNames: ['w-[15%]']
            },
            {
              columnKey: 'actions',
              widthClassNames: ['ml-auto'],
              onRender: (palette: TableRecord) => {
                return (
                  <div className="flex gap-3 pr-3">
                    <DuplicateAssetButton
                      onDuplicateAsset={handleDuplicatePalette(palette)}
                    />
                    <DeleteAssetButton
                      onDeleteAsset={handleDeletePalette(palette)}
                    />
                  </div>
                );
              }
            }
          ]}
        />
      )}
      <ConfirmDeleteModalComponent
        isOpen={confirmDelete}
        entityName={paletteToDelete.system_name}
        onConfirm={handleConfirmedDeletePalette}
        onCancel={() => setConfirmDelete(false)}
      />
      <CannotDeleteModalComponent
        entityName={paletteToDelete?.system_name ?? ''}
        dependencies={dependencies}
        isOpen={showCannotDeleteModal}
        onCancel={() => setShowCannotDeleteModal(false)}
      />
    </>
  );
}
